import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, ArrowLeft, Plus, FileText } from 'lucide-react';

export default function ThreePLMarketplacePage() {
  const [providers, setProviders] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [tab, setTab] = useState('rfqs');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/v2/3pl/providers').then(r => r.json()).catch(() => ({ providers: [] })),
      fetch('/api/v2/3pl/rfqs').then(r => r.json()).catch(() => ({ rfqs: [] })),
    ]).then(([p, r]) => {
      setProviders(p.providers || []);
      setRfqs(r.rfqs || []);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#10B981]" />
            <span className="font-semibold">3PL Marketplace</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-3">Agentic 3PL bidding.</h1>
          <p className="text-lg text-[#94A3B8] max-w-3xl">
            Pick a 3PL in days, not weeks. Post an RFQ from chat. Our agents draft your
            requirements; provider agents draft their bids; you compare ranked by Cost × Speed × Reputation.
            Contract via DocuSign. Then full operational integration: inventory, allocation,
            pick tickets, manifests, labels, POD.
          </p>
        </div>

        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setTab('rfqs')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === 'rfqs' ? 'bg-[#3EF1C7] text-[#0B1120]' : 'bg-[#0D1628] border border-[#22304A] text-[#94A3B8]'
            }`}
          >
            Active RFQs ({rfqs.length})
          </button>
          <button
            onClick={() => setTab('providers')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === 'providers' ? 'bg-[#3EF1C7] text-[#0B1120]' : 'bg-[#0D1628] border border-[#22304A] text-[#94A3B8]'
            }`}
          >
            Providers ({providers.length})
          </button>
          <Link
            to="/3pl/onboard"
            className="ml-auto px-4 py-2 bg-[#10B981] text-[#0B1120] rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> 3PL? Sign up
          </Link>
        </div>

        {loading ? (
          <div className="text-[#94A3B8]">Loading...</div>
        ) : tab === 'rfqs' ? (
          rfqs.length === 0 ? (
            <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-12 text-center">
              <FileText className="w-12 h-12 text-[#3EF1C7] mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No active RFQs yet</h3>
              <p className="text-sm text-[#94A3B8] max-w-md mx-auto">
                Text us "I need a 3PL for 5K orders/mo, US East Coast, apparel" to post your first RFQ.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rfqs.map(rfq => (
                <div key={rfq.rfq_id} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-semibold">{rfq.title}</div>
                      <div className="text-xs text-[#64748B] mt-1">Posted {rfq.posted_at}</div>
                    </div>
                    <span className="text-xs px-2 py-1 bg-[#3EF1C7]/20 text-[#3EF1C7] rounded font-mono">
                      {rfq.status}
                    </span>
                  </div>
                  <div className="text-sm text-[#94A3B8] mb-3">{rfq.description}</div>
                  <div className="flex justify-between text-xs text-[#64748B]">
                    <span>{rfq.monthly_volume_orders?.toLocaleString()} orders/mo</span>
                    <span>{rfq.contract_length_months}mo contract</span>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          providers.length === 0 ? (
            <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-12 text-center">
              <Truck className="w-12 h-12 text-[#10B981] mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No 3PLs onboarded yet</h3>
              <p className="text-sm text-[#94A3B8] mb-4 max-w-md mx-auto">
                Are you a 3PL? Sign up to receive RFQs from sellers and build a verifiable reputation
                at commerceindex.ai/3pl/your-name.
              </p>
              <Link to="/3pl/onboard" className="inline-block px-4 py-2 bg-[#10B981] text-[#0B1120] rounded-lg font-semibold">
                Sign up as a 3PL
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {providers.map(p => (
                <div key={p.provider_id} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                  <div className="font-semibold mb-1">{p.display_name}</div>
                  <div className="text-xs text-[#64748B] mb-3">{p.legal_name}</div>
                  <div className="text-sm text-[#94A3B8] mb-3">
                    {(p.capabilities || []).slice(0, 3).join(' • ')}
                  </div>
                  <div className="text-xs text-[#64748B]">
                    {(p.locations || []).length} location(s) · capacity {p.monthly_capacity_orders?.toLocaleString()} orders/mo
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
